import { BaseRepository } from './BaseRepository';

/**
 * Call Script entity type
 */
export interface CallScript {
  id: string;
  company_id: string;
  name: string;
  script_type: 'greeting' | 'inquiry' | 'claim' | 'closing' | 'followup' | 'other';
  content: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Repository for managing call scripts
 * Provides CRUD operations for agent call scripts
 */
export class CallScriptRepository extends BaseRepository<CallScript> {
  constructor() {
    super({ tableName: 'call_scripts', primaryKey: 'id' });
  }

  /**
   * Find all scripts for a specific company
   */
  async findByCompany(companyId: string, activeOnly: boolean = false): Promise<CallScript[]> {
    const filters: Record<string, any> = { company_id: companyId };
    if (activeOnly) {
      filters.active = true;
    }

    return this.findAll({
      filters,
      orderBy: { column: 'script_type', ascending: true }
    });
  }

  /**
   * Find scripts by type
   */
  async findByType(
    companyId: string, 
    scriptType: CallScript['script_type']
  ): Promise<CallScript[]> {
    return this.findAll({
      filters: { company_id: companyId, script_type: scriptType },
      orderBy: { column: 'name', ascending: true }
    });
  }

  /**
   * Toggle script active status
   */
  async toggleActive(id: string): Promise<CallScript> {
    const script = await this.findById(id);
    if (!script) {
      throw new Error('Script not found');
    }

    return this.update(id, { active: !script.active });
  }

  /**
   * Get count of active scripts by type
   */
  async countByType(
    companyId: string, 
    scriptType: CallScript['script_type']
  ): Promise<number> {
    return this.count({ 
      company_id: companyId, 
      script_type: scriptType,
      active: true 
    });
  }
}

// Export singleton instance
export const callScriptRepository = new CallScriptRepository();
